"use client";

/**
 * Install Rankbist as an app (PWA, Phase 25). Chrome, Edge and Android offer
 * a one-click install through the browser's own prompt; iPhone and iPad need
 * Share → Add to Home Screen, so we show those steps instead. Nothing shows
 * once the app is installed or after "Not now" (for 30 days).
 */
import { useEffect, useState } from "react";
import { Download, Share, Smartphone, X } from "lucide-react";
import { Btn, Card } from "@/components/app/ui";
import { isIos, isStandalone } from "@/lib/push";
import { getConsent, onConsent } from "@/lib/track";
import { usePathname } from "next/navigation";

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// The browser fires this once, early; keep it for whoever asks later.
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

const DISMISS_KEY = "rb_install_dismissed";
const DISMISS_DAYS = 30;

function dismissedRecently(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

type Mode = "installed" | "prompt" | "ios" | "none";

function useInstall(): { mode: Mode; install: () => Promise<void> } {
  const [, tick] = useState(0);
  const [standalone, setStandalone] = useState(false);
  const [ios, setIos] = useState(false);
  useEffect(() => {
    // Read the browser once mounted; the server render never knows these.
    /* eslint-disable react-hooks/set-state-in-effect */
    setStandalone(isStandalone());
    setIos(isIos());
    /* eslint-enable react-hooks/set-state-in-effect */
    const l = () => tick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  const mode: Mode = standalone ? "installed" : deferred ? "prompt" : ios ? "ios" : "none";
  async function install() {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice.catch(() => undefined);
    } catch {
      /* the browser refused the prompt (already used, or not allowed now) */
    }
    deferred = null;
    tick((n) => n + 1);
  }
  return { mode, install };
}

function IosSteps() {
  return (
    <ol className="mt-1.5 list-decimal space-y-0.5 pl-5 text-[12.5px] text-rb-muted">
      <li>Tap <Share size={12} className="inline align-[-2px]" /> <strong>Share</strong> in Safari.</li>
      <li>Choose <strong>Add to Home Screen</strong>.</li>
    </ol>
  );
}

/**
 * The install pop-up, shown on the website and in the dashboard (mounted in
 * the root layout). It waits until the visitor has answered the cookie
 * banner so the two never stack, sits centred at the bottom like that
 * banner, and lifts above the phone tab bar.
 */
const HIDE_ON = ["/login", "/signup", "/forgot-password", "/offline", "/email-preferences", "/r/"];

export function InstallNudge() {
  const { mode, install } = useInstall();
  const pathname = usePathname() || "/";
  const [show, setShow] = useState(false);
  const [consented, setConsented] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConsented(getConsent() != null);
    return onConsent((c) => setConsented(c != null));
  }, []);
  const hidden = HIDE_ON.some((p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : p + "/"));
  useEffect(() => {
    if (mode !== "prompt" && mode !== "ios") return;
    if (!consented || dismissedRecently()) return;
    const t = setTimeout(() => setShow(true), 15000); // not on first paint
    return () => clearTimeout(t);
  }, [mode, consented]);
  if (!show || hidden || (mode !== "prompt" && mode !== "ios")) return null;
  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage blocked */
    }
    setShow(false);
  };
  return (
    <div role="dialog" aria-label="Install the Rankbist app" data-no-track
      className="fixed inset-x-0 bottom-[calc(80px+env(safe-area-inset-bottom))] z-[55] px-3 md:bottom-6 md:left-1/2 md:right-auto md:w-[calc(100%-48px)] md:max-w-[760px] md:-translate-x-1/2 md:px-0">
      <div className="rb-scroll relative max-h-[min(70dvh,560px)] overflow-y-auto overscroll-contain rounded-[22px] border border-black/5 bg-white p-5 shadow-[0_24px_60px_-20px_rgba(20,15,51,0.45)] sm:p-6">
        <button type="button" onClick={dismiss} aria-label="Close" className="absolute right-3 top-3 rounded-lg p-1.5 text-rb-faint hover:bg-rb-surface-2 hover:text-rb-ink"><X size={16} /></button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-start gap-3 pr-6 sm:pr-0">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#B49CFF] to-[#6a52ff] text-white"><Smartphone size={20} /></span>
            <div className="min-w-0">
              <div className="text-[15px] font-bold text-rb-ink">Install the Rankbist app</div>
              <p className="mt-0.5 text-[13px] leading-snug text-rb-muted">Open Rankbist from your home screen or dock, and get notifications even when the browser is closed.</p>
              {mode === "ios" && <IosSteps />}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2 sm:pr-7 max-sm:[&>*]:flex-1">
            <Btn variant="ghost" onClick={dismiss}>Not now</Btn>
            {mode === "prompt" && <Btn icon={Download} onClick={() => install().then(dismiss)}>Install</Btn>}
            {mode === "ios" && <Btn onClick={dismiss}>Got it</Btn>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Settings → Install the app. */
export function InstallAppCard() {
  const { mode, install } = useInstall();
  return (
    <Card id="app" title="Rankbist app" subtitle="Install Rankbist on your phone or computer" icon={Smartphone}>
      {mode === "installed" ? (
        <p className="text-[13.5px] text-rb-muted">You&apos;re using the installed app.</p>
      ) : mode === "prompt" ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-[52ch] text-[13.5px] text-rb-muted">Open Rankbist from your home screen or dock like any other app, with notifications even when the browser is closed.</p>
          <Btn size="sm" icon={Download} onClick={install}>Install the app</Btn>
        </div>
      ) : mode === "ios" ? (
        <div className="text-[13.5px] text-rb-muted">
          Add Rankbist to your Home Screen to use it like an app and get notifications:
          <IosSteps />
        </div>
      ) : (
        <p className="text-[13.5px] text-rb-muted">
          In Chrome or Edge, use the install icon in the address bar (or the browser menu → <strong>Install Rankbist</strong>). On iPhone, use Safari → Share → Add to Home Screen.
        </p>
      )}
    </Card>
  );
}
